import { Session, SessionAnalysis } from '../domain/Session';
import {
    ISessionRepository,
    ISessionAnalysisService,
    SessionListParams,
    SessionListResult,
} from '../domain/ISessionRepository';
import { SessionsAPI } from '../infrastructure/api/sessions.api';

/**
 * Adapter that implements ISessionRepository using the existing SessionsAPI.
 * This allows application-layer code to depend on the interface, not the concrete API.
 */
class SessionRepositoryAdapter implements ISessionRepository {
    async getById(id: string, params?: { log_view?: boolean }): Promise<Session> {
        const res = await SessionsAPI.get(id, params);
        return res.data;
    }

    async create(data: Partial<Session>): Promise<Session> {
        const res = await SessionsAPI.create(data);
        return res.data;
    }

    async update(id: string, data: Partial<Session>): Promise<Session> {
        const res = await SessionsAPI.update(id, data);
        return res.data;
    }

    async remove(id: string): Promise<void> {
        await SessionsAPI.delete(id);
    }

    async listByTherapist(therapistId: string, params?: SessionListParams): Promise<SessionListResult> {
        const res = await SessionsAPI.list_by_therapist(therapistId, params);
        return res.data;
    }

    async listByPatient(patientId: string, params?: SessionListParams): Promise<SessionListResult> {
        const res = await SessionsAPI.list_by_patient(patientId, params);
        return res.data;
    }

    async listByCouplePatient(couplePatientId: string, params?: SessionListParams): Promise<SessionListResult> {
        const res = await SessionsAPI.list_by_couple_patient(couplePatientId, params);
        return res.data;
    }
}

class SessionAnalysisServiceAdapter implements ISessionAnalysisService {
    async analyze(sessionId: string): Promise<SessionAnalysis> {
        const res = await SessionsAPI.analyze(sessionId);
        return res.data;
    }
}

/**
 * Application service that orchestrates session operations.
 * Hooks should call this service instead of SessionsAPI directly.
 */
export class SessionApplicationService {
    constructor(
        private readonly repo: ISessionRepository = new SessionRepositoryAdapter(),
        private readonly analysisService: ISessionAnalysisService = new SessionAnalysisServiceAdapter(),
    ) {}

    async getSessionById(id: string, params?: { log_view?: boolean }): Promise<Session> {
        return this.repo.getById(id, params);
    }

    async createSession(data: Partial<Session>): Promise<Session> {
        return this.repo.create(data);
    }

    async updateSession(id: string, data: Partial<Session>): Promise<Session> {
        return this.repo.update(id, data);
    }

    async deleteSession(id: string): Promise<void> {
        return this.repo.remove(id);
    }

    async listSessionsByTherapist(therapistId: string, params?: SessionListParams): Promise<SessionListResult> {
        return this.repo.listByTherapist(therapistId, params);
    }

    async listSessionsByPatient(patientId: string, params?: SessionListParams): Promise<SessionListResult> {
        return this.repo.listByPatient(patientId, params);
    }

    async listSessionsByCouplePatient(couplePatientId: string, params?: SessionListParams): Promise<SessionListResult> {
        return this.repo.listByCouplePatient(couplePatientId, params);
    }

    async requestAnalysis(sessionId: string): Promise<SessionAnalysis> {
        return this.analysisService.analyze(sessionId);
    }
}

// Module-level singleton — avoids provider proliferation
export const sessionService = new SessionApplicationService();
